import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Image,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '@/hooks/use-auth';
import { useProfile } from '@/hooks/useProfile';
import { useCreateHousehold, useJoinHousehold } from '@/hooks/useHousehold';
import { setOnboardingCompleted } from '@/lib/onboarding-storage';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { removeCachedProfile } from '@/lib/profile-cache';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { parseInviteTokenFromInput, PENDING_INVITE_KEY } from '@/lib/invite-links';
import { supabase } from '@/lib/supabase';

const CONTENT_MAX_WIDTH = 864;

async function validateInviteToken(token: string): Promise<boolean> {
  const { data } = await supabase.rpc('get_household_id_for_invite_token', {
    token,
  } as any);
  return !!data;
}

type HouseholdChoice = 'create' | 'join' | null;

export default function OnboardingScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { profile, refreshProfile, updateProfile } = useProfile();
  const { createHousehold, loading: creatingHousehold, error: createError } = useCreateHousehold();
  const { joinHousehold, loading: joiningHousehold, error: joinError } = useJoinHousehold();

  const [step, setStep] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [householdChoice, setHouseholdChoice] = useState<HouseholdChoice>(null);
  const [householdName, setHouseholdName] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteValidating, setInviteValidating] = useState(false);
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  // Pre-fill display name from profile or user metadata
  useEffect(() => {
    if (!user) return;
    let name = profile?.display_name ?? '';
    if (!name && user.user_metadata?.display_name) {
      name = user.user_metadata.display_name;
    }
    if (!name && user.user_metadata?.full_name) {
      name = (user.user_metadata.full_name as string).trim().split(' ')[0];
    }
    if (name && !displayName) setDisplayName(name);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when user/profile change; displayName is write target
  }, [user?.id, profile?.display_name, user?.user_metadata]);

  // Pre-fill invite from pending deep link; default household step to "join" when we have an invite
  useEffect(() => {
    AsyncStorage.getItem(PENDING_INVITE_KEY).then((raw) => {
      const token = raw?.trim() ? parseInviteTokenFromInput(raw.trim()) || raw.trim() : '';
      if (token && !inviteToken) {
        setInviteToken(raw!.trim());
        if (householdChoice === null) setHouseholdChoice('join');
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to read pending invite from storage
  }, []);


  const TOTAL_STEPS = 5; // Welcome, DisplayName, HouseholdChoice, CreateName OR JoinToken, (implicit complete)

  const canProceed = () => {
    if (step === 0) return true;
    if (step === 1) return displayName.trim().length > 0;
    if (step === 2) return householdChoice !== null;
    if (step === 3 && householdChoice === 'create') return householdName.trim().length > 0;
    if (step === 3 && householdChoice === 'join') return inviteValid === true;
    return false;
  };

  const handleNext = () => {
    if (step === 0) {
      setStep(1);
      return;
    }
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      setStep(3);
      return;
    }
    if (step === 3) {
      handleComplete();
    }
  };

  /** Validate token, join household, complete onboarding and go to home. Used when step 3 is "join". */
  const handleJoinAndContinue = async () => {
    if (!user?.id) return;
    const token = parseInviteTokenFromInput(inviteToken);
    if (!token) {
      setInviteError('Please paste an invite link or token.');
      return;
    }
    setInviteValidating(true);
    setInviteError(null);
    setCompleteError(null);
    try {
      const valid = await validateInviteToken(token);
      if (!valid) {
        setInviteError('This invite link is invalid or has expired.');
        return;
      }
      const h = await joinHousehold(token);
      if (!h) {
        setInviteError(joinError?.message ?? 'Could not join household.');
        return;
      }
      await AsyncStorage.removeItem(PENDING_INVITE_KEY);
      if (displayName.trim()) {
        await updateProfile({ display_name: displayName.trim() });
      }
      removeCachedProfile(user.id);
      await refreshProfile();
      await setOnboardingCompleted(user.id);
      router.replace('/(tabs)');
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Something went wrong. Please try again.';
      setInviteError(msg);
      console.error('[Onboarding] Join and continue error:', msg, err);
    } finally {
      setInviteValidating(false);
    }
  };

  const handleComplete = async () => {
    if (!user?.id) return;
    setCompleting(true);
    setCompleteError(null);

    try {
      if (displayName.trim()) {
        await updateProfile({ display_name: displayName.trim() });
      }

      if (householdChoice === 'create') {
        const h = await createHousehold(householdName.trim() || 'My Household');
        if (!h) throw new Error(createError?.message ?? 'Could not create household');
        await AsyncStorage.removeItem(PENDING_INVITE_KEY);
      } else if (householdChoice === 'join' && inviteValid) {
        const token = parseInviteTokenFromInput(inviteToken);
        const h = await joinHousehold(token);
        if (!h) throw new Error(joinError?.message ?? 'Could not join household');
        await AsyncStorage.removeItem(PENDING_INVITE_KEY);
      }

      removeCachedProfile(user.id);
      await refreshProfile();
      await setOnboardingCompleted(user.id);

      router.replace('/(tabs)');
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Something went wrong. Please try again.';
      setCompleteError(msg);
      console.error('[Onboarding] Complete error:', msg, err);
    } finally {
      setCompleting(false);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSkip = () => {
    if (!user?.id) return;
    AsyncStorage.removeItem(PENDING_INVITE_KEY);
    setOnboardingCompleted(user.id).then(() => router.replace('/(tabs)'));
  };

  const isFirstStep = step === 0;
  const isLastStep = step === 3;

  return (
    <View style={styles.container}>
      <LinearGradient colors={[...Brand.primaryGradient]} style={styles.background} />

      {!isFirstStep && (
        <View style={[styles.skipRowWrapper, { paddingTop: Math.max(insets.top, 16) }]}>
          <View style={styles.skipRow}>
            <TouchableOpacity testID="onboarding-back-btn" onPress={handleBack} style={styles.backButton} activeOpacity={0.7}>
              <Ionicons name="arrow-back" size={20} color={Brand.primary} />
              <Text style={styles.backText}>Back</Text>
            </TouchableOpacity>
            <TouchableOpacity testID="onboarding-skip-btn" onPress={handleSkip} style={styles.skipButton} activeOpacity={0.7}>
              <Text style={styles.skipText}>Skip</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: isFirstStep ? Math.max(insets.top, 24) : 16 },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Step 0: Welcome */}
          {step === 0 && (
            <View style={styles.welcomeSection} testID="onboarding-welcome-step">
              <View style={styles.welcomeLogoWrapper}>
                <Image
                  source={require('@/assets/images/icon.png')}
                  style={styles.welcomeLogo}
                  resizeMode="contain"
                  accessibilityLabel="Sage Chef logo"
                />
              </View>
              <Text style={styles.welcomeTitle} testID="onboarding-welcome-heading">Welcome back to Sage Chef</Text>
              <Text style={styles.welcomeSubtitle}>Let&apos;s get you set up.</Text>
              <Text style={styles.welcomeDescription}>
                Your all-in-one solution for meal planning, recipe management, and smart grocery
                shopping. Just a few steps and you&apos;re ready to go.
              </Text>

              <View style={styles.valueCards}>
                <View style={styles.valueCard}>
                  <View style={styles.valueCardIcon}>
                    <Ionicons name="book-outline" size={24} color={Neutrals.white} />
                  </View>
                  <View style={styles.valueCardContent}>
                    <Text style={styles.valueCardTitle}>Save & Organize Recipes</Text>
                    <Text style={styles.valueCardDesc}>
                      Build your personal recipe library with tags and categories
                    </Text>
                  </View>
                </View>
                <View style={styles.valueCard}>
                  <View style={[styles.valueCardIcon, styles.valueCardIcon2]}>
                    <Ionicons name="cart-outline" size={24} color={Neutrals.white} />
                  </View>
                  <View style={styles.valueCardContent}>
                    <Text style={styles.valueCardTitle}>One-Tap Grocery Lists</Text>
                    <Text style={styles.valueCardDesc}>
                      Add all recipe ingredients to your list instantly
                    </Text>
                  </View>
                </View>
                <View style={styles.valueCard}>
                  <View style={[styles.valueCardIcon, styles.valueCardIcon3]}>
                    <Ionicons name="people-outline" size={24} color={Neutrals.white} />
                  </View>
                  <View style={styles.valueCardContent}>
                    <Text style={styles.valueCardTitle}>Share with Household</Text>
                    <Text style={styles.valueCardDesc}>
                      Collaborate with family members in real-time
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Step 1: Display Name */}
          {step === 1 && (
            <View style={styles.stepSection}>
              <View style={styles.stepIconContainer}>
                <Ionicons name="person" size={40} color={Brand.primary} />
              </View>
              <Text style={styles.stepTitle}>Tell us about yourself</Text>
              <Text style={styles.stepSubtitle}>How should we greet you?</Text>
              <View style={styles.card}>
                <Input
                  testID="onboarding-display-name-input"
                  label="Display name"
                  placeholder="e.g. Alex"
                  value={displayName}
                  onChangeText={setDisplayName}
                  autoCapitalize="words"
                  autoComplete="name"
                  style={styles.input}
                />
                <Text style={styles.helperText}>This name will appear in the app</Text>
              </View>
            </View>
          )}

          {/* Step 2: Household Choice */}
          {step === 2 && (
            <View style={styles.stepSection}>
              <View style={styles.stepIconContainer}>
                <Ionicons name="people" size={40} color={Brand.primary} />
              </View>
              <Text style={styles.stepTitle}>Household</Text>
              <Text style={styles.stepSubtitle}>
                {inviteToken.trim()
                  ? 'Join this household from your invite, or create your own instead.'
                  : 'Create a new household or join one with an invite'}
              </Text>
              <View style={styles.choiceRow}>
                <TouchableOpacity
                  testID="onboarding-choice-join"
                  style={[
                    styles.choiceCard,
                    householdChoice === 'join' && styles.choiceCardSelected,
                  ]}
                  onPress={() => setHouseholdChoice('join')}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="person-add-outline"
                    size={32}
                    color={householdChoice === 'join' ? Brand.primary : Neutrals.textSecondary}
                  />
                  <Text
                    style={[
                      styles.choiceCardTitle,
                      householdChoice === 'join' && styles.choiceCardTitleSelected,
                    ]}
                  >
                    Join from invite
                  </Text>
                  <Text style={styles.choiceCardDesc}>
                    {inviteToken.trim() ? 'Use your invite link' : 'I have an invite link'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="onboarding-choice-create"
                  style={[
                    styles.choiceCard,
                    householdChoice === 'create' && styles.choiceCardSelected,
                  ]}
                  onPress={() => setHouseholdChoice('create')}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="add-circle-outline"
                    size={32}
                    color={householdChoice === 'create' ? Brand.primary : Neutrals.textSecondary}
                  />
                  <Text
                    style={[
                      styles.choiceCardTitle,
                      householdChoice === 'create' && styles.choiceCardTitleSelected,
                    ]}
                  >
                    Create your own
                  </Text>
                  <Text style={styles.choiceCardDesc}>Set up a new household</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Step 3: Create household name OR Join with token */}
          {step === 3 && householdChoice === 'create' && (
            <View style={styles.stepSection}>
              <View style={styles.stepIconContainer}>
                <Ionicons name="home" size={40} color={Brand.primary} />
              </View>
              <Text style={styles.stepTitle}>Name your household</Text>
              <Text style={styles.stepSubtitle}>e.g. Smith Family</Text>
              <View style={styles.card}>
                <Input
                  testID="onboarding-household-name-input"
                  label="Household name"
                  placeholder="e.g. Smith Family"
                  value={householdName}
                  onChangeText={setHouseholdName}
                  style={styles.input}
                  editable={!creatingHousehold}
                />
                {createError && (
                  <Text style={styles.errorText}>{createError.message}</Text>
                )}
              </View>
            </View>
          )}

          {step === 3 && householdChoice === 'join' && (
            <View style={styles.stepSection}>
              <View style={styles.stepIconContainer}>
                <Ionicons name="link" size={40} color={Brand.primary} />
              </View>
              <Text style={styles.stepTitle}>Join with invite</Text>
              <Text style={styles.stepSubtitle}>Paste your invite link or token, then join and go to home.</Text>
              <View style={styles.card}>
                <Input
                  label="Invite link or code"
                  placeholder="Paste link or token"
                  value={inviteToken}
                  onChangeText={(t) => {
                    setInviteToken(t);
                    setInviteValid(null);
                    setInviteError(null);
                  }}
                  style={styles.input}
                  editable={!inviteValidating}
                />
                <Button
                  onPress={handleJoinAndContinue}
                  disabled={inviteValidating || !inviteToken.trim() || joiningHousehold}
                  loading={inviteValidating || joiningHousehold}
                  style={styles.validateButton}
                >
                  {inviteValidating || joiningHousehold ? 'Joining…' : 'Join and continue'}
                </Button>
                {inviteError && <Text style={styles.errorText}>{inviteError}</Text>}
                {joinError && <Text style={styles.errorText}>{joinError.message}</Text>}
              </View>
            </View>
          )}

          {/* Progress */}
          <View style={styles.progressRow}>
            {[0, 1, 2, 3].map((i) => (
              <View
                key={i}
                style={[
                  styles.progressDot,
                  i === step && styles.progressDotActive,
                  i < step && styles.progressDotDone,
                ]}
              />
            ))}
          </View>
          <Text style={styles.stepLabel}>
            Step {step + 1} of {TOTAL_STEPS}
          </Text>
        </ScrollView>

        <View style={[styles.footerWrapper, { paddingBottom: Math.max(insets.bottom, 24) }]}>
          <View style={styles.footer}>
            {completeError && (
              <Text style={styles.completeErrorText}>{completeError}</Text>
            )}
            {!(step === 3 && householdChoice === 'join') && (
              <Button
                testID="onboarding-next-btn"
                onPress={handleNext}
                disabled={!canProceed() || completing || creatingHousehold || joiningHousehold}
                loading={completing || creatingHousehold || joiningHousehold}
                style={styles.primaryButton}
              >
                {completing || creatingHousehold || joiningHousehold
                  ? 'Setting up…'
                  : isLastStep
                    ? 'Get Started'
                    : 'Continue'}
              </Button>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingBottom: 24,
    maxWidth: CONTENT_MAX_WIDTH,
    alignSelf: 'center',
    width: '100%',
  },
  skipRowWrapper: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: 16,
  },
  skipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
  },
  skipButton: { paddingVertical: 8 },
  skipText: {
    color: Neutrals.textSecondary,
    fontSize: 14,
    fontWeight: '500',
  },

  // Welcome
  welcomeSection: { alignItems: 'center', paddingTop: 16 },
  welcomeLogoWrapper: {
    marginBottom: 24,
    width: 120,
    height: 120,
    borderRadius: 24,
    overflow: 'hidden',
  },
  welcomeLogo: {
    width: '100%',
    height: '100%',
  },
  welcomeTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontSize: 20,
    fontWeight: '600',
    color: Brand.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  welcomeDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 8,
  },
  valueCards: { gap: 16, width: '100%', maxWidth: 400 },
  valueCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)',
  },
  valueCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    backgroundColor: Brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  valueCardIcon2: {
    backgroundColor: Brand.primaryGradientStrong[1],
  },
  valueCardIcon3: {
    backgroundColor: Brand.primaryDark,
  },
  valueCardContent: { flex: 1 },
  valueCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 4,
  },
  valueCardDesc: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    lineHeight: 20,
  },

  // Step sections
  stepSection: { paddingTop: 8 },
  stepIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: primaryAlpha(0.2),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  stepTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
  },
  card: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)',
  },
  input: { marginBottom: 12 },
  helperText: {
    fontSize: 12,
    color: Neutrals.textSecondary,
    marginTop: -8,
  },
  errorText: {
    fontSize: 14,
    color: Neutrals.destructiveMuted,
    marginTop: 8,
  },
  validateButton: { marginTop: 8 },
  validBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  validText: {
    fontSize: 14,
    color: Brand.primary,
    fontWeight: '500',
  },

  // Choice cards
  choiceRow: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  choiceCard: {
    flex: 1,
    minWidth: 140,
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 20,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.08)',
    alignItems: 'center',
  },
  choiceCardSelected: {
    borderColor: Brand.primary,
    backgroundColor: primaryAlpha(0.08),
  },
  choiceCardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginTop: 12,
    marginBottom: 4,
  },
  choiceCardTitleSelected: {
    color: Brand.primary,
  },
  choiceCardDesc: {
    fontSize: 14,
    color: Neutrals.textSecondary,
  },

  // Progress
  progressRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginTop: 32,
  },
  progressDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Neutrals.border,
  },
  progressDotActive: {
    width: 32,
    backgroundColor: Brand.primary,
  },
  progressDotDone: {
    backgroundColor: Brand.primary,
  },
  stepLabel: {
    fontSize: 12,
    color: Neutrals.textMuted,
    textAlign: 'center',
    marginTop: 8,
  },

  footerWrapper: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: 16,
  },
  footer: {
    paddingTop: 16,
  },
  completeErrorText: {
    fontSize: 14,
    color: Neutrals.destructiveMuted,
    marginBottom: 12,
    textAlign: 'center',
  },
  primaryButton: {
    minHeight: 52,
  },
});
